import { useMemo } from 'react';
import { useShowSvcPrice } from 'context/ShowSvcPriceContext';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';

import { Flex, Stack, Text, useTheme } from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  removeAddons,
  removeProvider,
  removeService,
  setServiceActiveStep
} from 'store/reducers/bookingAppointmentSlice';
import { setRedirectFlow } from 'store/reducers/userSlice';

import { formatCurrency } from 'utils';

import { BookServiceSteps, SearchParameters, ServiceInterface } from 'types';

import { Button } from 'ui';
import { AppointmentDetailsItem } from './components';

interface AppointmentDetailsListProps {
  onMoveToServiceStep: () => void;
  isMoveAppt: boolean;
}

const AppointmentDetailsList = ({
  onMoveToServiceStep,
  isMoveAppt
}: AppointmentDetailsListProps) => {
  const dispatch = useAppDispatch();
  const showSvcPrice = useShowSvcPrice();
  const { selectedProviders, selectedAddons, selectedServices } =
    useAppSelector((state) => state.bookingAppointment);

  const {
    colors: {
      palette: {
        booking: {
          stepTime: { apptDetailsList }
        }
      }
    }
  } = useTheme();

  const [_, setServiceIds] = useQueryState(
    SearchParameters.Services,
    parseAsArrayOf(parseAsString).withDefault([])
  );
  const selectedServicesNumber = selectedServices.length;
  const selectedAppointmentsNumber =
    Object.values(selectedAddons).flat(2).length;
  const selectedItemNumber =
    selectedServicesNumber + selectedAppointmentsNumber;
  const isSelectedServicesExist = selectedServicesNumber > 0;
  const serviceText = selectedItemNumber === 1 ? 'service' : 'services';
  const totalAmount = selectedServices.reduce(
    (acc, service) => acc + service.svc_price,
    0
  );

  const selectedServicesIds = useMemo(
    () =>
      selectedServices.map((service: ServiceInterface) => String(service.id)),
    [selectedServices]
  );

  const handleRemoveService = (id: number) => () => {
    dispatch(removeService(id));
    dispatch(removeProvider({ service: id }));
    dispatch(removeAddons({ service: id }));

    const updatedServiceIds = selectedServicesIds.filter(
      (svcId) => svcId !== id.toString()
    );
    setServiceIds(updatedServiceIds);

    if (selectedServicesNumber === 1) {
      onMoveToServiceStep();
      setServiceActiveStep(BookServiceSteps.Services);
      dispatch(setRedirectFlow(null));
    }
  };

  return (
    <Stack
      bg={apptDetailsList.background}
      px={{ base: '13px', md: '16px', xl: '22px' }}
      pt='35px'
      pb='40px'
      gap='10px'
      borderRadius={6}
    >
      {isSelectedServicesExist ? (
        <>
          <Flex
            w='100%'
            justifyContent='space-between'
            alignItems='center'
            pr={{ md: '20px', lg: '32px', xl: '41px' }}
            mb='30px'
            gap='30px'
          >
            <Text
              fontSize={{ base: '14px', md: '16px', xl: '18px' }}
              lineHeight='180%'
              pl={{ base: '17px', md: 0 }}
            >
              {`You have ${selectedItemNumber} ${serviceText} in your appointment`}
            </Text>
            {showSvcPrice && (
              <Stack alignItems='flex-end' gap={0}>
                <Text
                  fontSize={{ base: '11px', md: '12px', xl: '14px' }}
                  textTransform='uppercase'
                  color={apptDetailsList.estimatedTotalLabelColor}
                  whiteSpace='nowrap'
                  pr={{ base: '5px', sm: 0 }}
                >
                  Estimated Total
                </Text>
                <Text
                  fontSize={{ base: '24px', md: '26px', xl: '29px' }}
                  color={apptDetailsList.estimatedTotalColor}
                >
                  {formatCurrency(totalAmount)}
                </Text>
              </Stack>
            )}
          </Flex>

          {selectedServices.map((service) => {
            const currentProvider = selectedProviders[service.id];

            return (
              <AppointmentDetailsItem
                key={service.id}
                service={service}
                provider={currentProvider}
                onRemoveService={handleRemoveService(service.id)}
                isMoveAppt={isMoveAppt}
              />
            );
          })}

          <Button
            display={isMoveAppt ? 'none ' : 'block'}
            variant='outline'
            px='48px'
            width='max-content'
            onClick={onMoveToServiceStep}
            mx='auto'
            mt='30px'
          >
            Add another service
          </Button>
        </>
      ) : (
        <Flex minHeight='300px' alignItems='center' justifyContent='center'>
          <Text fontSize='16px'>You have no services.</Text>
        </Flex>
      )}
    </Stack>
  );
};

export default AppointmentDetailsList;
